import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import {
    DialogContent, DialogTitle, Stack, Typography, Card, CardContent,
    Grid, IconButton, Tooltip, Dialog, Divider, Box, Chip
} from '@mui/material'
import { EditOutlined, CloseOutlined, LinkOutlined, FileTextOutlined, OrderedListOutlined } from '@ant-design/icons'

import { getPermissionKeys } from 'utils/util'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { EVENT_BANNER_SLIDE_DETAIL_INIT, EVENT_BANNER_SLIDE_UPDATE_INIT } from 'models/events/banner.event'
import { getImageUrl } from 'utils/helpers'

export const BannerSlideDetailDialog = () => {
    const [slide, setSlide] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        // payload is the full slide object (already loaded in Banner.Detail's slides list)
        return eventbus.on(EVENT_BANNER_SLIDE_DETAIL_INIT, (slideData) => setSlide(slideData))
    }, [eventbus])

    return (
        <Dialog
            open={slide != null}
            maxWidth="sm"
            fullWidth
            scroll={'body'}
            onClose={() => setSlide(null)}
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
            {slide && <BannerSlideDetail slide={slide} onClose={() => setSlide(null)} />}
        </Dialog>
    )
}

const BannerSlideDetail = ({ slide, onClose }) => {
    const eventbus = useEventBusContext()
    const permissionsKeys = getPermissionKeys('banner')

    const DetailRow = ({ icon, label, value, fullWidth = false }) => (
        <Grid item size={{ xs: 12, sm: fullWidth ? 12 : 6 }}>
            <Box sx={{
                p: 2, borderRadius: 2, bgcolor: 'grey.50',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'grey.100' },
                display: 'flex', gap: '10px', alignItems: 'baseline'
            }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {icon} {label}:
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {value || '-'}
                </Typography>
            </Box>
        </Grid>
    )

    return (
        <>
            <DialogTitle sx={{ pb: 2, pt: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2} justifyContent="space-between">
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{slide.title}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                label={slide.status === 'active' ? 'Active' : 'Inactive'}
                                size="small"
                                color={slide.status === 'active' ? 'success' : 'error'}
                                sx={{ fontWeight: 600 }}
                            />
                            <Chip label={`Order: ${slide.sort_order}`} size="small" variant="outlined" icon={<OrderedListOutlined />} />
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={1}>
                        {permissionsKeys.can_update && (
                            <Tooltip title="Edit Slide">
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        onClose()
                                        eventbus.fire(EVENT_BANNER_SLIDE_UPDATE_INIT, slide)
                                    }}
                                    sx={{ bgcolor: 'primary.lighter', '&:hover': { bgcolor: 'primary.light' } }}
                                >
                                    <EditOutlined />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton onClick={onClose} sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                            <CloseOutlined />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Stack spacing={3}>
                    <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ position: 'relative', paddingTop: '45%', bgcolor: 'grey.50' }}>
                            {slide.image_url ? (
                                <Box
                                    component="img"
                                    src={getImageUrl(slide.image_url)}
                                    alt={slide.title}
                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                    <FileTextOutlined style={{ fontSize: 48, color: '#bbb' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No Image Available</Typography>
                                </Box>
                            )}
                        </Box>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FileTextOutlined />
                                Slide Information
                            </Typography>
                            <Grid container spacing={2}>
                                <DetailRow label="Title" value={slide.title} fullWidth />
                                <DetailRow icon={<LinkOutlined />} label="Link" value={slide.link} fullWidth />
                            </Grid>
                        </CardContent>
                    </Card>

                    {slide.description && (
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Description</Typography>
                                <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'text.secondary' }}>
                                        {slide.description}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            </DialogContent>
        </>
    )
}

BannerSlideDetail.propTypes = {
    slide: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
}

export default BannerSlideDetail